import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { cloneProduct, deleteProduct, getProducts, reorderProducts } from '../../lib/api';

const CATEGORIES = {
  deck: 'Deck',
  fence: 'Çit Sistemleri',
  garden_gate: 'Bahçe Kapı Sistemleri',
  facade: 'Cephe Sistemleri-Vinylbond',
  pergola: 'Pergola',
  baffle_ceiling: 'Baffle Tavan / Seperatör',
  flat_panel: 'Flat Paneller',
};

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderMode, setReorderMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts();
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(error.message || 'Ürünler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const moveProduct = async (currentIndex, newIndex) => {
    if (newIndex < 0 || newIndex >= products.length) return;

    try {
      const items = Array.from(products);
      const [movedItem] = items.splice(currentIndex, 1);
      items.splice(newIndex, 0, movedItem);

      const updatedItems = items.map((item, index) => ({
        ...item,
        display_order: index + 1,
      }));

      setProducts(updatedItems);
      await reorderProducts(updatedItems.map((item) => ({ id: item.id, display_order: item.display_order })));
      toast.success('Sıralama başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error.message || 'Sıralama güncellenirken bir hata oluştu');
      fetchProducts();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await deleteProduct(id);
      setProducts((prev) => prev.filter((product) => product.id !== id));
      toast.success('Ürün başarıyla silindi');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.message || 'Ürün silinirken bir hata oluştu');
    }
  };

  const handleClone = async (product) => {
    try {
      setLoading(true);
      const clonedProduct = await cloneProduct(product.id);
      setProducts((prev) => [...prev, clonedProduct].sort((a, b) => a.display_order - b.display_order));
      toast.success('Ürün başarıyla klonlandı');
      navigate(`/admin/products/${clonedProduct.id}/edit`);
    } catch (error) {
      console.error('Error cloning product:', error);
      toast.error(error.message || 'Ürün klonlanırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center p-4">Yükleniyor...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Ürünler</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setReorderMode(!reorderMode)}
            className={`px-4 py-2 rounded-md ${
              reorderMode ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
            } text-white transition-colors duration-200`}
          >
            {reorderMode ? 'Sıralamayı Bitir' : 'Sıralamayı Düzenle'}
          </button>
          <Link
            to="/admin/products/new"
            className="bg-[#dbc582] hover:bg-[#cbb374] text-white px-4 py-2 rounded-md transition-colors duration-200"
          >
            Yeni Ürün Ekle
          </Link>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {reorderMode && (
                <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sırala
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sıra
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Başlık
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kategori
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                İşlemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product, index) => (
              <tr key={product.id} className="hover:bg-gray-50 transition-colors duration-200">
                {reorderMode && (
                  <td className="w-20 px-4 py-2">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveProduct(index, index - 1)}
                        disabled={index === 0}
                        className={`p-1 rounded ${
                          index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Yukarı Taşı"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveProduct(index, index + 1)}
                        disabled={index === products.length - 1}
                        className={`p-1 rounded ${
                          index === products.length - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Aşağı Taşı"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </td>
                )}
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{product.display_order}</td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{product.title}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{CATEGORIES[product.category]}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                    className="text-[#dbc582] hover:text-[#cbb374] transition-colors duration-200"
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleClone(product)}
                    className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                  >
                    Klonla
                  </button>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:text-red-900 transition-colors duration-200"
                  >
                    Sil
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductList;
